import React from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { COUNTRY_OPTIONS, isItaly } from '../../lib/countries';
import { subjectTypeToCard } from './StepTypeSelect';

/**
 * Step 1 · Details form (dynamic per selected card).
 *
 *  Privato               → nome, cognome, CF, data di nascita, residenza,
 *                          CAP, città, provincia, paese, telefono
 *  Azienda/Professionista → referente, ragione sociale, P.IVA, CF se diverso,
 *                          sede, CAP, città, provincia, paese, telefono
 *                          (+ IT: SDI/PEC opzionali, usati per fatturazione)
 */

const Field = ({ id, label, prefilled, children }) => (
  <div>
    <Label htmlFor={id} className="mb-1 flex items-center gap-1.5 text-[12px] font-medium">
      {label}
      {prefilled && (
        <span className="rounded bg-amber-500/15 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wide gold-text">
          Pre
        </span>
      )}
    </Label>
    {children}
  </div>
);

const TextField = ({ id, label, value, onChange, prefilled, type = 'text', placeholder, maxLength, testId, mono }) => (
  <Field id={id} label={label} prefilled={prefilled}>
    <Input
      id={id}
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`h-10 ${mono ? 'font-mono uppercase' : ''}`}
      data-testid={testId}
    />
  </Field>
);

const CountrySelect = ({ id, label, value, onChange, testId }) => {
  const { t } = useTranslation('onboarding');
  return (
    <Field id={id} label={label}>
      <select
        id={id}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
        data-testid={testId}
      >
        <option value="">{t('fields.country_placeholder')}</option>
        {COUNTRY_OPTIONS.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>
    </Field>
  );
};

const StepDetails = ({ subjectType, form, onFieldChange, prefilledKeys }) => {
  const { t } = useTranslation('onboarding');
  const preSet = new Set(prefilledKeys || []);
  const country = form.country_iso;
  const italy = isItaly(country);
  const isPrivato = subjectTypeToCard(subjectType) === 'individual';

  const setField = (key) => (v) => onFieldChange(key, v);
  const req = (label) => `${label} *`;

  const PhoneField = (
    <Field id="f-phone" label={req(t('fields.phone'))} prefilled={preSet.has('phone')}>
      <div className="phone-input-wrap">
        <PhoneInput
          international
          defaultCountry={italy || !country ? 'IT' : undefined}
          value={form.phone || ''}
          onChange={(v) => setField('phone')(v || '')}
          data-testid="fld-phone"
        />
      </div>
    </Field>
  );

  const AddressBlock = (
    <>
      <div className="md:col-span-2">
        <TextField
          id="f-line1"
          label={req(isPrivato ? t('fields.address_residence') : t('fields.address_seat'))}
          value={form.address_line1}
          onChange={setField('address_line1')}
          testId="fld-address-line1"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <TextField id="f-cap" label={req(t('fields.postal_code'))}
          value={form.postal_code} onChange={setField('postal_code')} maxLength={12} testId="fld-postal" />
        <TextField id="f-city" label={req(t('fields.city'))}
          value={form.city} onChange={setField('city')} testId="fld-city" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <TextField id="f-prov" label={req(t('fields.province'))}
          value={form.province} onChange={setField('province')} maxLength={6} testId="fld-province" />
        <CountrySelect id="f-country" label={req(t('fields.country'))}
          value={form.country_iso} onChange={setField('country_iso')} testId="fld-country" />
      </div>
    </>
  );

  return (
    <div data-testid="wizard-step-details" data-subject-type={isPrivato ? 'individual' : 'company'}>
      <div className="mb-4 mt-6">
        <h2 className="text-lg font-semibold text-foreground">
          {t(isPrivato ? 'details_step.privato_title' : 'details_step.azienda_title')}
        </h2>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          {t('details_step.prefilled_note')}
        </p>
      </div>

      {isPrivato ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextField id="f-first" label={req(t('fields.first_name'))}
            value={form.first_name} onChange={setField('first_name')}
            prefilled={preSet.has('first_name')} testId="fld-first-name" />
          <TextField id="f-last" label={req(t('fields.last_name'))}
            value={form.last_name} onChange={setField('last_name')}
            prefilled={preSet.has('last_name')} testId="fld-last-name" />
          <TextField id="f-cf" label={req(italy || !country ? t('fields.codice_fiscale') : t('fields.tax_id_foreign'))}
            value={form.codice_fiscale} onChange={setField('codice_fiscale')} mono maxLength={32}
            testId="fld-codice-fiscale" />
          <TextField id="f-dob" label={req(t('fields.date_of_birth'))} type="date"
            value={form.date_of_birth} onChange={setField('date_of_birth')} testId="fld-date-of-birth" />
          {AddressBlock}
          {PhoneField}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextField id="f-first" label={req(t('fields.reference_first_name'))}
            value={form.first_name} onChange={setField('first_name')}
            prefilled={preSet.has('first_name')} testId="fld-first-name" />
          <TextField id="f-last" label={req(t('fields.reference_last_name'))}
            value={form.last_name} onChange={setField('last_name')}
            prefilled={preSet.has('last_name')} testId="fld-last-name" />
          <div className="md:col-span-2">
            <TextField id="f-biz" label={req(t('fields.business_name'))}
              value={form.business_name} onChange={setField('business_name')} testId="fld-business-name" />
          </div>
          <TextField id="f-vat" label={req(italy || !country ? t('fields.vat_number') : t('fields.vat_number_foreign'))}
            value={form.vat_number} onChange={setField('vat_number')} mono testId="fld-vat" />
          <TextField id="f-cf" label={t('fields.codice_fiscale_optional')}
            value={form.codice_fiscale} onChange={setField('codice_fiscale')} mono maxLength={32}
            testId="fld-codice-fiscale" />
          {AddressBlock}
          {PhoneField}
          {italy && (
            <>
              <TextField id="f-sdi" label={t('fields.sdi_code')}
                value={form.sdi_code} onChange={setField('sdi_code')} mono maxLength={7} testId="fld-sdi" />
              <TextField id="f-pec" label={t('fields.pec_email')} type="email"
                value={form.pec_email} onChange={setField('pec_email')} testId="fld-pec" />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StepDetails;
